import React, { useEffect } from 'react';
import './DeletionProgressModal.css';

interface DeletionProgressModalProps {
  isOpen: boolean;
  isDeleting: boolean;
  totalDocuments: number;
  deletedCount: number;
  currentFileName?: string;
  onComplete?: () => void;
}

const DeletionProgressModal: React.FC<DeletionProgressModalProps> = ({
  isOpen,
  isDeleting,
  totalDocuments,
  deletedCount,
  currentFileName,
  onComplete
}) => {

  // Countdown timer for auto-close
  const [countdown, setCountdown] = React.useState(2);
  
  const progressPercentage = totalDocuments > 0 ? (deletedCount / totalDocuments) * 100 : 0;
  const isComplete = !isDeleting && deletedCount === totalDocuments;

  // Auto-close modal after 2 seconds when deletion is complete
  useEffect(() => {
    if (isComplete && onComplete) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isComplete, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="deletion-modal-overlay">
      <div className="deletion-modal-container">
        {/* Header */}
        <div className="deletion-modal-header">
          <div className="deletion-modal-icon">
            {isComplete ? (
              <svg className="success-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg className="deleting-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M10 11V17M14 11V17" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <div className="deletion-modal-title">
            <h3>
              {isComplete 
                ? 'Documents Deleted Successfully' 
                : isDeleting 
                  ? 'Deleting Documents...' 
                  : 'Preparing to Delete'
              }
            </h3>
            <p className="deletion-modal-subtitle">
              {isComplete 
                ? `Successfully deleted ${deletedCount} document${deletedCount !== 1 ? 's' : ''}`
                : `${deletedCount} of ${totalDocuments} document${totalDocuments !== 1 ? 's' : ''} deleted`
              }
            </p>
          </div>
        </div>

        {/* Progress Section */}
        <div className="deletion-modal-content">
          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${progressPercentage}%`,
                  backgroundColor: isComplete ? '#10B981' : '#3B82F6'
                }}
              />
            </div>
            <div className="progress-text">
              {Math.round(progressPercentage)}%
            </div>
          </div>

          {/* Current File */}
          {isDeleting && currentFileName && (
            <div className="current-file">
              <div className="current-file-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"
                    stroke="#6B7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="current-file-name">Deleting: {currentFileName}</span>
            </div>
          )}

          {/* Loading Animation */}
          {isDeleting && (
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isComplete && (
          <div className="deletion-modal-footer">
            <button
              className="deletion-complete-btn"
              onClick={onComplete}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Done ({countdown}s)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletionProgressModal; 